// OPS CALCULATOR — teklif/maliyet aracı (admin panel sekmesi)
//
// Hesabın tamamı OpsCalcEngine.js'te (saf, test edilebilir). Bu dosya yalnızca
// ekran ve ağ: koordinat ve rüzgâr çekimi + form.
//
// NEDEN AĞ SERBEST: offline-first kuralı KOKPİT içindir (pilot internet
// beklemez, İlke 4). Bu araç admin panelinde, masada, çevrimiçi kullanılır.
//
// KAYNAKLAR
//   ICAO -> koordinat : `airports` TABLOSU (lat/lon orada zaten var)
//   Seyir rüzgârı     : api.open-meteo.com, 200 hPa (~FL390, FL400 referansımız)
//
// 🔴 NEDEN KOORDİNAT TABLODAN, WEB'DEN DEĞİL (12 Agu, sahada bulundu):
// Önce aviationweather.gov denendi — curl ile çalışıyor ama TARAYICIDAN
// ÇALIŞMIYOR: `Access-Control-Allow-Origin` başlığı göndermiyor, tarayıcı
// isteği bloke ediyor ("Failed to fetch"). curl CORS uygulamaz, tarayıcı
// uygular; o yüzden komut satırında geçen bir servis panelde patlayabiliyor.
// Open-Meteo `allow-origin: *` gönderiyor, o yüzden rüzgâr web'den geliyor.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SecimKutusu } from './GosterGizle';   // seçim kutusu tasarım S1 (5 Eki 2026)
import { supabase } from '../supabaseClient';
import { normTime } from './inputFormat';
import HarfliGiris from './HarfliGiris';
import {
  greatCircleNM, initialBearing, intermediatePoint, windComponentKt,
  computeLeg, computeItinerary, hhmm, KMH_PER_KT, AC_GLF4,
  LEVELS, HPA_FOR_FL, profileForLevel, fitPhaseProfile, SID_STAR_ALLOWANCE_MIN } from './OpsCalcEngine';
import { SAYILI, hepsiniOku, okumaDenetimi } from './kesikOkuma';   // 24 ay şartı — parça parça, düşen/kesik okuma söylenir (6 Eki 2026)
import OkumaHatasi from './OkumaHatasi';

const WIND_SAMPLES = 5;      // büyük daire üzerinde örneklenecek nokta sayısı

// ── ağ ───────────────────────────────────────────────────────────────────────

async function fetchCoords(icaos) {
  const ids = icaos.filter(Boolean).map(s => s.toUpperCase());
  const { data, error } = await supabase
    .from('airports').select('icao,name,lat,lon').in('icao', ids);
  if (error) throw new Error(`Airport lookup failed: ${error.message}`);
  const out = {};
  (data || []).forEach(a => {
    // Koordinatı olmayan satırı SESSİZCE kabul etme — bulunamadı say, çağıran
    // ismini vererek söylesin (İlke 1: eksik veriyle hesap yapma).
    if (a.lat != null && a.lon != null) {
      out[a.icao.toUpperCase()] = { lat: +a.lat, lon: +a.lon, name: a.name || '' };
    }
  });
  return out;
}

/**
 * Rotanın seyir seviyesindeki ORTALAMA rüzgâr bileşeni (kt).
 * +kuyruk / -kafa. Büyük daire üzerinde WIND_SAMPLES nokta örneklenir,
 * her noktada yerel rota açısına göre bileşen alınır, ortalanır.
 * Hata olursa null döner — çağıran "sakin hava" der, SESSİZ KALMAZ.
 */
async function fetchWindComponent(a, b, whenISO, flLevel = 400) {
  try {
    const pts = [];
    for (let i = 1; i <= WIND_SAMPLES; i++) {
      const f = i / (WIND_SAMPLES + 1);
      const p = intermediatePoint(a.lat, a.lon, b.lat, b.lon, f);
      // yerel rota açısı: o noktadan varışa doğru
      pts.push({ ...p, course: initialBearing(p.lat, p.lon, b.lat, b.lon) });
    }
    const lat = pts.map(p => p.lat.toFixed(3)).join(',');
    const lon = pts.map(p => p.lon.toFixed(3)).join(',');
    // RUZGAR SECILEN SEVIYEDEN (15 Agu 2026): eskiden sabit 200 hPa (~FL385)
    // cekiliyordu — FL150'de ucan bir bacak icin FL385 ruzgari kullanmak
    // hesabi sessizce bozardi. Seviye -> basinc yuzeyi eslesmesi ISA basinc
    // irtifasindan (HPA_FOR_FL), API'nin sundugu yuzeylerden en yakini.
    const hpa = HPA_FOR_FL[flLevel] ?? 200;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
                `&hourly=wind_speed_${hpa}hPa,wind_direction_${hpa}hPa&forecast_days=7&timezone=UTC`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    const locs = Array.isArray(data) ? data : [data];

    const target = new Date(whenISO);
    let sum = 0, n = 0;
    locs.forEach((loc, i) => {
      const h = loc.hourly; if (!h) return;
      // hedef saate en yakın dilim
      let best = -1, bestDiff = Infinity;
      h.time.forEach((t, k) => {
        const diff = Math.abs(new Date(t + 'Z') - target);
        if (diff < bestDiff) { bestDiff = diff; best = k; }
      });
      if (best < 0) return;
      const spdKt = h[`wind_speed_${hpa}hPa`]?.[best] / KMH_PER_KT;   // API km/h verir
      const dir = h[`wind_direction_${hpa}hPa`]?.[best];
      if (spdKt == null || dir == null) return;
      sum += windComponentKt(dir, spdKt, pts[i].course);
      n++;
    });
    return n ? sum / n : null;
  } catch {
    return null;
  }
}

// ── ekran ────────────────────────────────────────────────────────────────────

const card = { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 12 };
const lbl  = { fontSize: 10, fontWeight: 600, letterSpacing: 1, color: 'var(--muted, #8b93a7)', display: 'block', marginBottom: 4 };
const inp  = { width: '100%', padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'inherit', fontFamily: 'var(--mono)', fontSize: 13, boxSizing: 'border-box' };
const grid = (c) => ({ display: 'grid', gridTemplateColumns: `repeat(${c}, minmax(0,1fr))`, gap: 10 });
const h2   = { fontSize: 11, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10, color: 'var(--accent)' };

export default function OpsCalculator({ toast, customerId = null, readOnly = false }) {
  const [fleet, setFleet]   = useState([]);
  const [acId, setAcId]     = useState('');
  // ── TARIH + SAAT AYRI (16 Agu 2026, Serkan) ─────────────────────────────
  // *"Sistemde iOS ve web'de butun saatler 24 saatlik HH:MM olmali, tek tip
  //   method — admin panelde bazi yerlerde AM/PM seklinde girisler var."*
  //
  // 🔑 Kaynak `<input type="datetime-local">` idi: bu eleman TARAYICININ bolge
  // ayarina gore cizilir; 12 saatlik bolgede AM/PM cikar ve biz bunu koddan
  // kontrol EDEMEYIZ. Etikette "(UTC)" yazmasi da bir sey degistirmiyordu.
  // Saat artik uygulamanin kendi maskesinden geciyor (`normTime`, iOS'taki
  // `TimeInput.mask`'in web karsiligi) — bolge ayari devre disi.
  //
  // Birlesik deger ASLA state'te tutulmaz, TUREVDIR: iki alan ile birlesik
  // deger arasinda ikinci bir dogruluk kaynagi olusmasin (Ilke 2).
  const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

  // ── SEKTOR LISTESI (23 Agu 2026, Serkan) ────────────────────────────────
  // 🔑 *"OPS calculator'da ADD SECTOR ekleyelim, cunku her zaman basladigin
  //    yere donmuyorsun. Bugunki ucus ESB-BJV-NICE, daha sonra NICE-BJV-ESB —
  //    toplamda 4 bacak."* + *"outbound ve return degil, sektor ekleyelim."*
  //
  // Eski ekran GIDIS-DONUS kurgusundaydi: tek dep/dest, iki tarih, iki seviye,
  // iki yakit fiyati, iki ellecleme. Ucgen ve coklu rotalar o kaliba girmiyordu.
  //
  // Her sektor KENDI satirini tasir: meydanlar, tarih+saat, seviye, rota
  // uzamasi, kalkis meydaninin yakit fiyati, UPLIFT bayragi ve VARIS meydaninin
  // ellecelemesi. Ayni meydana iki kez ugranirsa iki AYRI satir — Serkan:
  // *"belki 2 kere git-gel yapabilirsin ama hizmet icerigi degisebilir."*
  const bosSektor = (fl = 400) => ({
    dep:'', dest:'', date:'', time:'', fl, conflict:false, extra:'',
    fuel:'', uplift:false, handling:'',
  });
  const [secs, setSecs] = useState([
    { ...bosSektor(400), uplift:true },   // ilk bacakta yakit alinir (varsayilan)
    bosSektor(410),                        // dogu/bati kurali: donus bir ust seviye
  ]);
  const setSec = (i, k, v) => setSecs(ls => ls.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const secAt = (x) => (x.date && HHMM.test(x.time) ? `${x.date}T${x.time}` : '');
  // ILK KALKIS MEYDANININ ELLECLEMESI — varis meydanlari satirlarda.
  const [handlingStart, setHandlingStart] = useState('');
  const [crew, setCrew]     = useState(2);
  // SEVIYE + YOLCU (15 Agu 2026, Serkan): "sadece seviye girecez" ve
  // "payload kisi basina 240 lb, ekrana yolcu sayisini da girelim".
  // ── SEVIYE GIDIS/DONUS AYRI (16 Agu 2026, Serkan) ───────────────────────
  // *"Ticari bir ucus hep iki bacak planlanir (donus yolcusuz da yapilabilir
  //   ama parasi musteriden alinir); gidis ve donus seviyeleri farklidir."*
  // Dogu/bati kurali geregi zaten farkli olmak ZORUNDA: gidis FL400 ise donus
  // FL410. Tek seviye secildiginde iki bacak da yanlis fiyatlandiriliyordu —
  // tirmanma/alcalma sureleri ve seyir TAS'i seviyeye bagli.
  const [pax, setPax]       = useState(0);
  // Seviye, rota uzamasi, yakit fiyati ve ellecleme artik SEKTOR SATIRINDA.
  const [c, setC] = useState({ hotel:'', perDiem:'', catering:'', doc:'' });
  const [res, setRes]   = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const setCost = (k, v) => setC(p => ({ ...p, [k]: v }));

  // 🔴 6 Eki 2026 (24 ay şartı, 2c-3). Uçak listesi ve performans örnekleri okumalarının `error`ü okunmuyordu: liste
  //    düşerse uçak seçilemiyor ama sebep söylenmiyordu; örnekler düşerse hesap SESSİZCE girilmiş katsayılara iniyor ve
  //    "henüz yeterli plan ölçümü yok" diyordu (okunamayan veri, olmayan veri değildir). Örnekler tek istekte okunuyordu.
  //    🔑 Serkan (6 Eki): *"ops calculator sadece anlık hesap makinası; herhangi bir arşivi yok, sistemde var olan OFP
  //    verilerini kullanıyor"* → **B**: örnekler okunamazsa hesap YİNE yapılır (AIRCRAFTS'ta girilmiş katsayılarla) ama
  //    ekranda kırmızı kutu bunu söyler, yanlış cümle çıkmaz. Uçak listesi okunamazsa seçilecek uçak yoktur — hesap yok.
  //    Örnekler PARÇA PARÇA, iki okuma da `okumaDenetimi`nden; kutu tek tip (OkumaHatasi).
  const [okumaHatasi, setOkumaHatasi] = useState(null);   // { filo?: [...], ornek?: [...] }
  const [yenile, setYenile] = useState(0);
  useEffect(() => {
    let iptal = false;
    (async () => {
      let q = supabase.from('aircraft').select('*', SAYILI).eq('active', true).order('registration');
      if (customerId) q = q.eq('customer_id', customerId);
      const { dustu, oku } = okumaDenetimi();
      let data;
      try { data = oku('AIRCRAFT LIST (aircraft)', await q); }
      catch (e) { dustu.push({ ad: 'AIRCRAFT LIST (aircraft)', metin: e?.message || String(e) }); }
      if (iptal) return;
      setOkumaHatasi(h => ({ ...h, filo: dustu.length ? dustu : null }));
      setFleet(dustu.length ? [] : (data || []));
      if (!dustu.length && data?.length === 1) setAcId(String(data[0].id));
    })();
    return () => { iptal = true; };
  }, [customerId, yenile]);

  const ac = fleet.find(a => String(a.id) === acId);

  // ── KENDI VERIMIZDEN PROFIL (16 Agu 2026, Serkan) ───────────────────────
  // "Sistemde yuklenmis ucuslardan kendi veri tabanimizi tutsak, tirmanis/duz
  //  ucus/alcalis yakit profilimizi ciksak."  ·  "Veri arttikca dogruluk
  //  artacak."
  // Havuz `aircraft_perf_samples` — plan silinse bile duran olcum kayitlari.
  // TESCIL BAZLI: her sirket yalniz kendi ucaginin verisini kullanir
  // (Serkan: "o baska sirketin ucagi").
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    if (!ac?.registration) { setSamples([]); setOkumaHatasi(h => ({ ...h, ornek: null })); return; }
    let iptal = false;
    (async () => {
      const s = await hepsiniOku(() => supabase.from('aircraft_perf_samples')
        .select('id,ete_min,trip_fuel_lb,cruise_fl,climb_min,desc_min,plan_id,fms_ident,measured_at', SAYILI)   // son ucu TEKILLESTIRME anahtari
        .eq('reg', ac.registration));
      if (iptal) return;
      const { dustu, oku } = okumaDenetimi();
      const data = oku('PERFORMANCE SAMPLES (aircraft_perf_samples)', s);
      setOkumaHatasi(h => ({ ...h, ornek: dustu.length ? dustu : null }));
      setSamples(dustu.length ? [] : (data || []));
    })();
    return () => { iptal = true; };
  }, [ac?.registration, yenile]);

  /** Havuzdan cozulen uc safha profili. Veri yetersizse `null` — o zaman
   *  asagida OFP'den olculen sabit katsayilara DUSULUR (uydurma yok). */
  const measured = useMemo(() => fitPhaseProfile(samples), [samples]);

  /** Uçağın performans katsayıları — girilmemişse hesap YAPILMAZ, söylenir.
   *  Oncelik: OLCULEN profil > uçağa girilmis sabit katsayilar. */
  const acParams = useMemo(() => {
    if (!ac) return null;
    if (measured) {
      return {
        label: ac.registration,
        // Seyir TAS'i seviyeden turer (`profileForLevel`), sarfiyat HAVUZDAN.
        cruiseTAS: ac.ops_cruise_tas ?? AC_GLF4.cruiseTAS,
        cruiseFF: measured.cruiseFF,
        climbMin: measured.climbMin, descMin: measured.descMin,
        climbFF: measured.climbFF,   descFF: measured.descFF,
        climbNM: ac.ops_climb_nm ?? 0, descNM: ac.ops_desc_nm ?? 0,
        climbFuel: measured.climbFF * (measured.climbMin / 60),
        descFuel:  measured.descFF  * (measured.descMin  / 60),
        groundFuel: ac.ops_ground_fuel ?? AC_GLF4.groundFuel,
        groundMin:  ac.ops_ground_min  ?? AC_GLF4.groundMin,
      };
    }
    if (!(ac.ops_cruise_tas && ac.ops_cruise_ff)) return null;
    return {
      label: ac.registration,
      cruiseTAS: ac.ops_cruise_tas, cruiseFF: ac.ops_cruise_ff,
      climbMin: AC_GLF4.climbMin, descMin: AC_GLF4.descMin,   // 25/25 STANDART (sirket usulu, ucaga bagli degil)
      climbNM: ac.ops_climb_nm ?? 0, climbFuel: ac.ops_climb_fuel ?? 0,
      descNM: ac.ops_desc_nm ?? 0,  descFuel: ac.ops_desc_fuel ?? 0,
      groundFuel: ac.ops_ground_fuel ?? 0, groundMin: ac.ops_ground_min ?? 0,
    };
  }, [ac, measured]);

  /** Secili seviyenin profili: tirmanma/alcalma sureleri, seyir TAS'i ve
   *  safha yakit oranlari. `acParams` ucagin KENDI olculen degerlerini tasir;
   *  seviye onun uzerine biner. */
  /** Her bacak KENDI seviyesinin profilini kullanir. */
  // Her sektor KENDI seviyesinin profilini kullanir (tirmanma/alcalma suresi ve
  // seyir TAS'i seviyeye bagli).


  const calculate = useCallback(async () => {
    setErr(''); setRes(null);
    if (okumaHatasi?.filo) { setErr('The aircraft list could not be read — press RETRY above.'); return; }
    if (!acParams) { setErr('Select an aircraft with performance data entered.'); return; }
    const list = secs.filter(x => x.dep || x.dest || x.date || x.time);
    if (!list.length) { setErr('Add at least one sector.'); return; }
    for (let i = 0; i < list.length; i++) {
      const x = list[i];
      if (x.dep.length !== 4 || x.dest.length !== 4) { setErr(`Sector ${i + 1}: enter 4-letter ICAO codes.`); return; }
      if (!secAt(x)) { setErr(`Sector ${i + 1}: enter date and time (HH:MM).`); return; }
    }
    // SIRA MANTIKLI OLMALI: her sektor bir oncekinden SONRA kalkar. Aksi halde
    // yer suresi ve gece sayisi anlamsiz cikar — sessizce yanlis hesaplamayiz.
    for (let i = 1; i < list.length; i++) {
      if (new Date(secAt(list[i])) <= new Date(secAt(list[i - 1]))) {
        setErr(`Sector ${i + 1} departs before sector ${i} — check the dates.`); return;
      }
    }

    setBusy(true);
    try {
      const icaos = [...new Set(list.flatMap(x => [x.dep, x.dest]))];
      const coords = await fetchCoords(icaos);
      const eksik = icaos.filter(k => !coords[k]);
      if (eksik.length) {
        throw new Error(`No coordinates for ${eksik.join(', ')} — add the airport first.`);
      }

      // Her bacak: buyuk daire + rota uzamasi + KENDI SEVIYESININ ruzgari.
      // Ruzgar ucusun ORTA anindan alinir; sure ruzgara bagli oldugu icin once
      // sakin havayla kabaca hesaplanip bir kez duzeltilir (eski davranis).
      const sonuc = [];
      for (let i = 0; i < list.length; i++) {
        const x = list[i];
        const A = coords[x.dep], B = coords[x.dest];
        const gc = greatCircleNM(A.lat, A.lon, B.lat, B.lon);
        const extra = x.conflict ? (+x.extra || 0) : 0;
        const acP = (acParams ? profileForLevel(x.fl, acParams) : null) ?? acParams;
        const kaba = computeLeg({ distanceNM: gc, extraNM: extra, ac: acP }).flightH;
        const orta = new Date(new Date(secAt(x)).getTime() + kaba / 2 * 3600000).toISOString();
        const w = await fetchWindComponent(A, B, orta, x.fl);
        const leg = computeLeg({ distanceNM: gc, extraNM: extra, windCompKt: w ?? 0, ac: acP });
        const departUTC = new Date(secAt(x)).toISOString();
        const arriveUTC = new Date(new Date(departUTC).getTime() + leg.flightMin * 60000).toISOString();
        sonuc.push({ ...x, A, B, gc, leg, departUTC, arriveUTC, windKnown: w != null,
                     fuelPricePerTonne: +x.fuel || 0, handling: +x.handling || 0 });
      }

      const trip = computeItinerary({
        sectors: sonuc,
        crewCount: +crew || 2,
        hotelNightly: +c.hotel || 0,
        perDiemDaily: +c.perDiem || 0,
        catering: +c.catering || 0,
        handlingStart: +handlingStart || 0,
        docHourly: +c.doc || (ac.ops_doc_hourly ?? 0),
      });

      setRes({ sectors: sonuc, trip });
    } catch (e) {
      setErr(e.message || 'Calculation failed.');
    }
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acParams, ac, secs, crew, c, handlingStart, okumaHatasi]);

  const money = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>OPS CALCULATOR</div>
      <div style={{ fontSize: 11, color: 'var(--amber)', marginBottom: 14 }}>
        ESTIMATE ONLY — great circle distance, standard climb/descent profile, forecast winds.
      </div>

      {/* ── ROTA ── */}
      <div style={card}>
        <div style={h2}>ROUTE &amp; SCHEDULE</div>
        <div style={grid(4)}>
          <div>
            <label style={lbl}>AIRCRAFT</label>
            <select style={inp} value={acId} onChange={e => setAcId(e.target.value)} disabled={readOnly}>
              <option value="">— select —</option>
              {fleet.map(a => <option key={a.id} value={a.id}>{a.registration} {a.ac_type ? `(${a.ac_type})` : ''}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>PAX</label>
            <HarfliGiris style={inp} value={pax} onChange={v => setPax(v.replace(/\D/g, ''))} disabled={readOnly} />
          </div>
          <div>
            <label style={lbl}>PAYLOAD (240 lb / PAX)</label>
            <input style={{ ...inp, opacity: .7 }} value={`${((+pax || 0) * 240).toLocaleString()} lb`} readOnly />
          </div>
          <div>
            <label style={lbl}>ZFW (BOW + PAYLOAD)</label>
            <input style={{ ...inp, opacity: .7 }} readOnly
                   value={ac?.ops_bow_lb
                     ? `${(ac.ops_bow_lb + (+pax || 0) * 240).toLocaleString()} lb`
                     : 'BOW not set'} />
          </div>
        </div>

        {/* ── SEKTORLER (23 Agu 2026, Serkan) ──────────────────────────────
            *"her zaman basladigin yere donmuyorsun ... outbound ve return
             degil, sektor ekleyelim."* Her satir kendi meydanlarini, saatini,
            seviyesini, yakit fiyatini ve elleclemesini tasir. Ayni meydana iki
            kez ugranirsa IKI AYRI satir — hizmet icerigi degisebilir. */}
        <div style={{ marginTop: 14 }}>
          <label style={lbl}>SECTORS — ALL TIMES UTC</label>
          <div style={{ display:'grid', gridTemplateColumns:'22px 1fr 1fr 1.5fr 1fr 1fr 1.1fr 62px 1.1fr 34px',
                        gap:6, fontSize:9, color:'var(--muted, #8b93a7)', letterSpacing:1, marginBottom:4 }}>
            <div /><div>DEP</div><div>DEST</div><div>DATE</div><div>TIME</div><div>LEVEL</div>
            <div>FUEL $/T</div><div>UPLIFT</div><div>HANDLING</div><div />
          </div>
          {secs.map((x, i) => (
            <div key={i} style={{ display:'grid',
                                  gridTemplateColumns:'22px 1fr 1fr 1.5fr 1fr 1fr 1.1fr 62px 1.1fr 34px',
                                  gap:6, marginBottom:6, alignItems:'center' }}>
              <div style={{ fontSize:11, fontFamily:'var(--mono)', color:'var(--muted, #8b93a7)', textAlign:'center' }}>{i + 1}</div>
              <HarfliGiris style={inp} value={x.dep} maxLength={4} placeholder="LTAC"
                     onChange={v => setSec(i, 'dep', v)} disabled={readOnly} />
              <HarfliGiris style={inp} value={x.dest} maxLength={4} placeholder="LFMN"
                     onChange={v => setSec(i, 'dest', v)} disabled={readOnly} />
              <input style={inp} type="date" value={x.date}
                     onChange={e => setSec(i, 'date', e.target.value)} disabled={readOnly} />
              <input style={inp} value={x.time} placeholder="HH:MM" maxLength={5}
                     onChange={e => setSec(i, 'time', normTime(e.target.value))} disabled={readOnly} />
              <select style={inp} value={x.fl} onChange={e => setSec(i, 'fl', +e.target.value)} disabled={readOnly}>
                {LEVELS.map(l => <option key={l} value={l}>FL{l}</option>)}
              </select>
              <HarfliGiris style={inp} value={x.fuel} placeholder="1350"
                     onChange={v => setSec(i, 'fuel', v.replace(/[^\d.]/g, ''))} disabled={readOnly} />
              {/* UPLIFT: bu meydanda yakit alindi mi? Alinmadiysa bu bacagin
                  yakiti EN SON uplift yapilan meydanin fiyatindan hesaplanir
                  (tankering). Eski tek dugmeli TANKERING bunun ozel halidir. */}
              <div style={{ textAlign:'center' }}>
                <SecimKutusu isaretli={!!x.uplift} devreDisi={readOnly} etiket="UPLIFT" testid={`uplift-${i}`}
                             onClick={() => setSec(i, 'uplift', !x.uplift)} />
              </div>
              <HarfliGiris style={inp} value={x.handling} placeholder="900"
                     onChange={v => setSec(i, 'handling', v.replace(/[^\d.]/g, ''))} disabled={readOnly} />
              <button style={{ ...inp, padding:'8px 4px', cursor: secs.length > 1 ? 'pointer' : 'default',
                               opacity: secs.length > 1 ? 1 : .35 }}
                      disabled={readOnly || secs.length <= 1}
                      onClick={() => setSecs(ls => ls.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}

          {/* ROTA UZAMASI — bacak bacak (Serkan, 12 Agu): catisma varsa NM eklenir */}
          {secs.map((x, i) => (
            <div key={`cf${i}`} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4, fontSize:10 }}>
              <span style={{ width:70, color:'var(--muted, #8b93a7)', fontFamily:'var(--mono)' }}>SEC {i + 1}</span>
              <SecimKutusu isaretli={!!x.conflict} devreDisi={readOnly} testid={`conflict-${i}`}
                           onClick={() => setSec(i, 'conflict', !x.conflict)}>CONFLICT ON ROUTE</SecimKutusu>
              {x.conflict && (
                <HarfliGiris style={{ ...inp, width:120 }} value={x.extra} placeholder="+NM"
                       onChange={v => setSec(i, 'extra', v.replace(/[^\d]/g, ''))} disabled={readOnly} />
              )}
            </div>
          ))}

          <div style={{ display:'flex', gap:10, alignItems:'flex-end', marginTop:8 }}>
            <button style={{ ...inp, width:150, cursor:'pointer' }} disabled={readOnly}
                    onClick={() => setSecs(ls => [...ls, bosSektor(ls[ls.length - 1]?.fl ?? 400)])}>
              + ADD SECTOR
            </button>
            <div style={{ width:180 }}>
              <label style={lbl}>HANDLING — FIRST DEPARTURE</label>
              <HarfliGiris style={inp} value={handlingStart} placeholder="900"
                     onChange={v => setHandlingStart(v.replace(/[^\d.]/g, ''))} disabled={readOnly} />
            </div>
          </div>
        </div>

        {/* ── KAYNAK SATIRI (16 Agu 2026, Serkan) ─────────────────────────
            "TCREC ucagina ait xxx sayida ucusun CLB-CRZ-DSC profili
             incelenerek ortalama fuel burn verileri elde edilmistir."
            Kaynagini soylemeyen bir ortalama, uydurma katsayidan farksizdir:
            ilk itirazda savunulamaz. Sayinin NEREDEN geldigi ekranda durur.
            ⚠️ "UCUS PLANI" denir, "UCUS" DENMEZ — veri OFP'den gelir,
            uculmus gercekten degil (Ilke 1). */}
        {/* Okuma düştüyse ya da eksik geldiyse kutu — SESSİZ inilmez (6 Eki 2026, Serkan B). */}
        {okumaHatasi?.filo && (
          <OkumaHatasi testid="ops-filo-hatasi" style={{ marginTop: 10 }} satirlar={okumaHatasi.filo} onRetry={() => setYenile(n => n + 1)}
            baslik="AIRCRAFT LIST COULD NOT BE READ — no aircraft can be chosen." />
        )}
        {okumaHatasi?.ornek && (
          <OkumaHatasi testid="ops-ornek-hatasi" style={{ marginTop: 10 }} satirlar={okumaHatasi.ornek} onRetry={() => setYenile(n => n + 1)}
            baslik="OFP PERFORMANCE SAMPLES COULD NOT BE READ — the calculation uses the cruise figures entered in AIRCRAFTS." />
        )}
        {measured && (
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6,
                        background: 'var(--accent-soft)', fontSize: 11, lineHeight: 1.5 }}>
            <b>{ac?.registration}</b> · {measured.n} UÇUŞ PLANI (OFP) İNCELENEREK
            {measured.nPhase < measured.n && <> · {measured.nPhase}'İNDE SAFHA ÖLÇÜMÜ</>}
            <br />
            TIRMANMA <b>{Math.round(measured.climbFF).toLocaleString()}</b> ·
            {' '}SEYİR <b>{Math.round(measured.cruiseFF).toLocaleString()}</b> ·
            {' '}ALÇALMA <b>{Math.round(measured.descFF).toLocaleString()}</b> lb/h
            {' '}<span style={{ color: 'var(--t3)' }}>(uyum R²={measured.r2.toFixed(3)})</span>
            <br />
            <span style={{ color: 'var(--t3)' }}>
              Alçalma dilimi, irtifa tahdidi olan planlarda kademeli iniş (düz uçuş)
              içerebilir — safha etiketi yaklaşıktır, toplamı etkilemez. ESTIMATE ONLY
            </span>
          </div>
        )}
        {ac && !measured && acParams && !okumaHatasi?.ornek && (
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--t3)' }}>
            {ac.registration}: henüz yeterli plan ölçümü yok — OFP'den girilen sabit
            katsayılar kullanılıyor. Her yeni plan yüklemesi profili iyileştirir.
          </div>
        )}

        {ac && !acParams && (
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--amber)' }}>
            {ac.registration}: performance data not entered — set cruise TAS and fuel flow on the Aircrafts tab.
          </div>
        )}

      </div>

      {/* ── MALIYET GIRDILERI ── */}
      <div style={card}>
        <div style={{ ...h2 }}>COSTS (USD)</div>
        {/* YAKIT FIYATI ve ELLECLEME artik SEKTOR SATIRINDA (23 Agu 2026).
            Eski tek dugmeli TANKERING kalkti: her sektorun kendi UPLIFT
            kutusu var ve kural su — uplift yoksa yakit EN SON uplift yapilan
            meydanin fiyatindan hesaplanir. Tek dugmeli hal bunun ozel
            durumudur (yalniz ilk sektorde uplift). */}
        <div style={{ fontSize: 11, color: 'var(--muted, #8b93a7)', marginBottom: 10 }}>
          Fuel price and handling are set per sector above. UPLIFT unticked = that
          sector burns fuel bought at the last airport where you uplifted (tankering).
        </div>
        <div style={grid(4)}>
          {[['catering', 'CATERING'], ['hotel', 'HOTEL / NIGHT / PERSON'],
            ['perDiem', 'PER DIEM / DAY / PERSON'],
            ['doc', `DOC / HOUR${ac?.ops_doc_hourly ? ` (${ac.ops_doc_hourly})` : ''}`]].map(([k, l]) => (
            <div key={k}>
              <label style={lbl}>{l}</label>
              <HarfliGiris style={inp} value={c[k]} onChange={v => setCost(k, v.replace(/[^\d.]/g, ''))} disabled={readOnly} />
            </div>
          ))}
          <div>
            <label style={lbl}>CREW</label>
            <HarfliGiris style={inp} value={crew} onChange={v => setCrew(v.replace(/\D/g, ''))} disabled={readOnly} />
          </div>
        </div>
      </div>

      <button onClick={calculate} disabled={busy || readOnly}
        style={{ padding: '10px 28px', borderRadius: 8, border: '1px solid var(--accent)', background: 'var(--accent-soft)',
                 color: 'inherit', fontWeight: 700, letterSpacing: 1, cursor: busy ? 'wait' : 'pointer', fontSize: 12 }}>
        {busy ? 'CALCULATING…' : 'CALCULATE'}
      </button>
      {err && <div style={{ marginTop: 10, color: 'var(--red)', fontSize: 12 }}>{err}</div>}

      {/* ── SONUC ── */}
      {res && (
        <div style={{ ...card, marginTop: 14 }}>
          <div style={h2}>RESULT</div>
          <div style={{ fontSize: 11, marginBottom: 10, fontFamily: 'var(--mono)' }}>
            {res.sectors.map(x => x.dep).concat(res.sectors[res.sectors.length - 1].dest).join(' → ')}
            {'  ·  '}<b>{res.trip.sectorCount} SECTORS</b>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--mono)' }}>
            <thead>
              <tr style={{ color: 'var(--muted,#8b93a7)', fontSize: 10, letterSpacing: 1 }}>
                {['#', 'SECTOR', 'GC NM', 'ROUTE NM', 'WIND', 'GS', 'FLT TIME', 'FUEL', 'FUEL $/T'].map(t =>
                  <th key={t} style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--border)' }}>{t}</th>)}
              </tr>
            </thead>
            <tbody>
              {res.sectors.map((x, i) => {
                const L = x.leg, y = res.trip.fuelPerSector[i];
                return (
                  <tr key={i}>
                    <td style={{ padding: '6px', color: 'var(--muted,#8b93a7)' }}>{i + 1}</td>
                    <td style={{ padding: '6px', fontWeight: 700 }}>{x.dep}–{x.dest}</td>
                    <td style={{ padding: '6px' }}>{Math.round(x.gc)}</td>
                    <td style={{ padding: '6px' }}>
                      {Math.round(L.routeNM)}{L.extraNM > 0 && <span style={{ color: 'var(--amber)' }}> (+{L.extraNM})</span>}
                    </td>
                    <td style={{ padding: '6px', color: x.windKnown ? (L.windCompKt < 0 ? 'var(--red)' : 'var(--green)') : 'var(--amber)' }}>
                      {x.windKnown ? `${L.windCompKt > 0 ? '+' : ''}${Math.round(L.windCompKt)} kt` : 'no data'}
                    </td>
                    <td style={{ padding: '6px' }}>{Math.round(L.groundSpeedKt)} kt</td>
                    <td style={{ padding: '6px', fontWeight: 700 }}>{hhmm(L.flightMin)}</td>
                    <td style={{ padding: '6px' }}>{Math.round(L.fuelLb).toLocaleString()} lb · {L.fuelTonnes.toFixed(2)} t</td>
                    {/* Yakit hangi meydanin fiyatindan alindi — tankering
                        gorunur olsun diye. Uplift yoksa satirda o yazar. */}
                    <td style={{ padding: '6px', color: x.uplift ? 'inherit' : 'var(--amber)' }}>
                      {money(y.fiyat)}{!x.uplift && ' (tankered)'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* SID & STAR PAYI GORUNUR OLMALI (27 Agu 2026, Serkan: "bunu text
              olarak belirtelim"). Sessizce eklenen bir pay, teklifi aciklayan
              kisinin bilmedigi bir sayi demektir. Nereden geldigi de yaziyor:
              olcum, tahmin degil. */}
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--t2)' }}>
            <b>+{SID_STAR_ALLOWANCE_MIN} min per sector — SID &amp; STAR allowance</b>{' '}
            (time and the fuel it burns), applied only to sectors that have a
            cruise phase — on very short hops the procedures are already inside
            climb and descent. Measured on TC-REC 27 AUG 2026 LTAC–EGLF–LTAC:
            the en-route track is almost exactly great circle (+7 / +15 NM); the
            difference comes from departure and arrival procedures (SID 60 / 48 NM,
            STAR + approach 64 / 24 NM). Checked against 6 out-and-back pairs.
          </div>

          {res.sectors.some(x => !x.windKnown) && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--amber)' }}>
              Wind data unavailable for {res.sectors.filter(x => !x.windKnown).length} sector(s) —
              calculated in still air. Total time is understated.
            </div>
          )}

          <div style={{ ...grid(4), marginTop: 14 }}>
            {[['FUEL', res.trip.fuelCost], ['HANDLING', res.trip.handling], ['CATERING', res.trip.catering],
              ['HOTEL', res.trip.hotelCost], ['PER DIEM', res.trip.perDiemCost], ['DOC', res.trip.docCost]].map(([t, v]) => (
              <div key={t}>
                <label style={lbl}>{t}</label>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{money(v)}</div>
              </div>
            ))}
            <div>
              <label style={lbl}>GROUND / PER DIEM</label>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
                {res.trip.groundHours.toFixed(1)} h · {res.trip.nights} nt · {res.trip.perDiemDays} d
              </div>
            </div>
            <div>
              <label style={lbl}>TOTAL FLIGHT / DOC</label>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
                {hhmm(res.trip.totalFlightH * 60)} · {hhmm(res.trip.totalDocH * 60)}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 30, alignItems: 'baseline' }}>
            <div>
              <label style={lbl}>TOTAL</label>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>{money(res.trip.total)}</div>
            </div>
            <div>
              <label style={lbl}>PER FLIGHT HOUR</label>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 16 }}>{money(res.trip.costPerFlightHour)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
